import { FooterHelp, Text, BlockStack, Badge, InlineStack } from "@medentic/polaris-react";
import TranslateLink from "./TranslateLink";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import {
  CLINIC_ROUTES,
  ROOTS,
  SETTINGS_ROUTES,
  USER_ROUTES,
} from "@app/config/constants";
import { useGetSingleCompanyDetails } from "@shared/hooks/common/useGetSingleCompanyDetails";
import { SandboxIcon } from "@medentic/polaris-icons";

function PageFooter() {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();
  const pathname = location.pathname;

  // Extract IDs from pathname as fallback when useParams doesn't work
  const clinicIdFromPath = pathname.match(/^\/c\/([^/]+)/)?.[1];
  const eventOrganizerIdFromPath = pathname.match(/^\/e\/([^/]+)/)?.[1];

  // Exclude "add" from being treated as a valid entity ID
  const selectedClinicId =
    (params.clinicId || clinicIdFromPath) !== "add"
      ? params.clinicId || clinicIdFromPath
      : undefined;
  const selectedEventOrganizerId =
    (params.eventOrganizerId || eventOrganizerIdFromPath) !== "add"
      ? params.eventOrganizerId || eventOrganizerIdFromPath
      : undefined;

  // Get the company ID (clinic or event organizer)
  const companyId = selectedClinicId || selectedEventOrganizerId;

  // Get company data using the hook
  const { company } = useGetSingleCompanyDetails(companyId, {
    enabled: Boolean(companyId),
    refetchOnMount: false,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  const getTranslationKey = () => {
    // Remove trailing slash if exists
    const path = pathname.replace(/\/$/, "");

    // Handle events routes
    if (path.startsWith(ROOTS.EVENTS)) {
      // /events/:eventOrganizerId/dashboard

      const eventsPathWithId = path.replace(ROOTS.EVENTS + "/", "");
      const afterEventsId = eventsPathWithId.replace(/^[^/]+\//, "");

      if (
        afterEventsId === "dashboard" ||
        afterEventsId === "" ||
        !eventsPathWithId.includes("/")
      ) {
        return "help_footer.events.dashboard";
      }
      // Future: add more event-related cases here
    }

    // Check clinic routes
    if (path.startsWith(ROOTS.CLINIC)) {
      const clinicPathWithId = path.replace(ROOTS.CLINIC + "/", "");
      const afterClinicId = clinicPathWithId.replace(/^[^/]+\//, "");

      if (afterClinicId.startsWith("patients/")) {
        if (afterClinicId === CLINIC_ROUTES.PATIENTS_DATA_MANAGEMENT)
          return "help_footer.patients.data_management";
        if (afterClinicId === CLINIC_ROUTES.PATIENTS_TAGS)
          return "help_footer.patients.tags";
        if (afterClinicId === CLINIC_ROUTES.PATIENTS_PROCEDURES)
          return "help_footer.patients.procedures";
        if (afterClinicId === CLINIC_ROUTES.PATIENTS_TREATMENT_PLANS)
          return "help_footer.patients.treatment_plans";
        if (afterClinicId === CLINIC_ROUTES.PATIENTS_MEDICATIONS)
          return "help_footer.patients.medications";
        if (afterClinicId.match(/^patients\/[\w-]+$/))
          return "help_footer.patients.details";
        return "help_footer.default";
      }

      if (afterClinicId === CLINIC_ROUTES.PERKS) return "help_footer.perks";
    }

    // Check user routes
    if (path.startsWith(ROOTS.USER)) {
      const userPath = path.replace(ROOTS.USER + "/", "");

      if (userPath === USER_ROUTES.DETAILS) return "help_footer.user.details";
      if (userPath === USER_ROUTES.CALENDER) return "help_footer.user.calendar";
      if (userPath === USER_ROUTES.SECURITY) return "help_footer.user.security";
      if (userPath === USER_ROUTES.COMPLIANCE)
        return "help_footer.user.compliance";
      if (userPath === USER_ROUTES.TEAMACCESS)
        return "help_footer.user.team_access";
    }

    // Check settings routes
    if (path.includes("/settings/")) {
      const settingsPath = path.split("/settings/")?.[1];

      if (settingsPath === SETTINGS_ROUTES.DETAILS)
        return "help_footer.company.details";
      if (settingsPath === SETTINGS_ROUTES.SUBSCRIPTION_PLAN)
        return "help_footer.company.billing";
      if (settingsPath === SETTINGS_ROUTES.MEMBERS_PERMISSIONS)
        return "help_footer.company.members_and_permissions";
      if (settingsPath === SETTINGS_ROUTES.COMPLIANCE)
        return "help_footer.company.compliance";
      if (settingsPath === SETTINGS_ROUTES.INVOICES)
        return "help_footer.company.invoices";
    }

    return "help_footer.default";
  };

  return (
    <div style={{ marginBottom: "20px" }}>
      <BlockStack gap="200" align="center">
        <FooterHelp>
          <TranslateLink text={t(getTranslationKey())} />
        </FooterHelp>
        {company?.isTest && (
          <InlineStack gap="200" align="center">
            <Badge tone="warning" icon={SandboxIcon}>
              {t("help_footer.test_company_badge")}
            </Badge>
            <Text as="p" tone="caution" alignment="center">
              {t("help_footer.test_company_message")}
            </Text>
          </InlineStack>
        )}
      </BlockStack>
    </div>
  );
}

export default PageFooter;
